import React, { useState, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  IconButton,
  Stack,
  Chip,
  useTheme,
} from "@mui/material";
import { Send, Mic, KeyboardArrowDown } from "@mui/icons-material";
import EmailIcon from "@mui/icons-material/Email";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import SchoolIcon from "@mui/icons-material/School";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import PaletteIcon from "@mui/icons-material/Palette";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import FlightIcon from "@mui/icons-material/Flight";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AddIcon from "@mui/icons-material/Add";
import { ChatContext } from "../context/chatcontext";
import { useAuth } from "../context/AuthContext";

const quickActions = [
  { key: "email", label: "Write Email", icon: EmailIcon },
  { key: "sports", label: "Sports", icon: SportsBasketballIcon },
  { key: "education", label: "Education", icon: SchoolIcon },
  { key: "launches", label: "New Launches", icon: RocketLaunchIcon },
  { key: "image", label: "Create Image", icon: PaletteIcon },
  { key: "learn", label: "Help Me Learn", icon: LightbulbIcon },
  { key: "investment", label: "Investment", icon: AccountBalanceIcon },
  { key: "restaurant", label: "Restaurant", icon: RestaurantIcon },
  { key: "travel", label: "Travel", icon: FlightIcon },
  { key: "fitness", label: "Health & Fitness", icon: FitnessCenterIcon },
  { key: "project", label: "Create Project Structure", icon: FolderOpenIcon },
];

// 15 prompts per sector
const sectorPrompts = {
  email: [
    "Write a professional email to a client introducing our new services and how they can benefit from them.",
    "Draft a follow-up email after a sales meeting thanking them for their time and summarizing next steps.",
    "Write a polite reminder email for an unpaid invoice that maintains a professional tone.",
    "Create an email to schedule a project kickoff call with agenda items and proposed dates.",
    "Write a thank-you email after a job interview expressing gratitude and enthusiasm for the role.",
    "Draft an internal update email to the team about project status with key milestones and blockers.",
    "Write an email announcing a product launch to customers highlighting key features and benefits.",
    "Create a welcome email for new users of our platform with onboarding steps and resources.",
    "Write an apology email for a service outage explaining what happened and what we're doing to prevent it.",
    "Draft an email to negotiate better pricing with a vendor emphasizing long-term partnership value.",
    "Write an email to request feedback from customers about their experience with our product.",
    "Draft a resignation email that is polite, professional, and expresses gratitude for the opportunity.",
    "Write an email to propose a collaboration with another company outlining mutual benefits.",
    "Draft a follow-up email after sending a proposal asking for feedback and next steps.",
    "Write a meeting recap email with clear action items, owners, and deadlines.",
  ],
  sports: [
    "Explain the basic rules of basketball for complete beginners, including scoring, fouls, and court positions.",
    "Create a comprehensive weekly workout plan for a football player focusing on strength, agility, and endurance.",
    "Summarize yesterday's major cricket matches with key highlights, scores, and standout performances.",
    "Suggest effective warm-up exercises before playing tennis to prevent injuries and improve performance.",
    "Explain offside rules in football in simple terms with examples to help beginners understand.",
    "Create a detailed practice drill plan for a basketball team covering shooting, defense, and ball handling.",
    "Recommend specific stretches for post-game recovery to reduce muscle soreness and improve flexibility.",
    "Write an inspiring motivational speech for a team before a championship final game.",
    "Suggest engaging indoor sports activities for a rainy day that can be done at home or in a gym.",
    "Explain the scoring system in badminton including points, sets, and match rules.",
    "Compare the cardio benefits of running vs cycling including calories burned, impact on joints, and muscle groups worked.",
    "Create a beginner's guide to fantasy sports explaining how to draft players, manage teams, and score points.",
    "Recommend the best books about leadership in sports that can inspire coaches and athletes.",
    "Explain how to track sports performance metrics using apps, wearables, and traditional methods.",
    "Suggest fun sports activities for a corporate team outing that promote teamwork and bonding.",
  ],
  education: [
    "Create a detailed 3-month study plan for competitive exams with daily schedules, weekly goals, and revision strategies.",
    "Explain the Pomodoro technique for focused studying including time blocks, breaks, and how to implement it effectively.",
    "Help me summarize a chapter for revision by extracting key concepts, important dates, and main ideas.",
    "Suggest effective note-taking methods for students including Cornell method, mind mapping, and digital tools.",
    "Create a balanced weekly timetable for a college student balancing classes, study time, and personal activities.",
    "Explain a difficult math concept in simple language with examples and step-by-step breakdowns.",
    "Generate comprehensive quiz questions for a history topic covering important events, dates, and figures.",
    "Suggest the best online resources to learn data science including courses, platforms, and practice projects.",
    "Help me write a compelling project abstract for my thesis that clearly outlines objectives, methodology, and expected outcomes.",
    "Draft a professional email to a professor requesting deadline extension with valid reasons and proposed new timeline.",
    "Explain active recall and how to use it effectively for better memory retention and exam preparation.",
    "Create flashcards content for vocabulary building with definitions, examples, and usage in sentences.",
    "Suggest proven strategies to avoid procrastination while studying including time management and motivation techniques.",
    "Help me create a well-structured presentation outline for a seminar with introduction, main points, and conclusion.",
    "Recommend 10 productive habits for students that improve learning, focus, and academic performance.",
  ],
  launches: [
    "Write an engaging launch announcement for a new SaaS product highlighting key features, benefits, and how to get started.",
    "Create a compelling social media post for a product launch with hashtags and a call-to-action.",
    "Draft a professional press release for a new feature rollout including quotes, benefits, and availability.",
    "Suggest a comprehensive launch email sequence for new users with welcome, onboarding, and feature highlights.",
    "Generate creative tagline options for a new mobile app that are memorable and convey the app's value proposition.",
    "Outline a detailed launch checklist for a tech product covering pre-launch, launch day, and post-launch activities.",
    "Create persuasive landing page copy for a beta launch that explains benefits and encourages sign-ups.",
    "Suggest attractive early-bird offers for launch campaigns that create urgency and reward early adopters.",
    "Write helpful onboarding tips for first-time users to ensure they get maximum value from the product.",
    "Draft comprehensive FAQs for a new product launch addressing common questions about features, pricing, and support.",
    "Suggest key KPIs to track after launch including user acquisition, engagement, retention, and revenue metrics.",
    "Create an engaging pitch script for a launch webinar that explains the product and encourages attendance.",
    "Write an informative internal memo about an upcoming launch with timeline, roles, and key talking points.",
    "Suggest creative influencer ideas for launch promotion including collaboration types and content themes.",
    "Generate engaging LinkedIn post ideas for launch week that showcase the product and encourage sharing.",
  ],
  image: [
    "Create a professional corporate office illustration with modern design, diverse team members, and collaborative workspace.",
    "Generate a stunning futuristic city skyline at night with neon lights, flying vehicles, and cyberpunk aesthetic.",
    "Write a prompt for a minimal, elegant logo for a tech startup with clean lines and modern typography.",
    "Create an abstract background for presentations with gradient colors, geometric shapes, and professional look.",
    "Write a prompt for a realistic portrait with professional studio lighting, high quality, and natural expression.",
    "Generate a product mockup on a clean white background with professional lighting and shadow effects.",
    "Create an isometric dashboard illustration showing data visualization, charts, and modern UI elements.",
    "Write a prompt for a cyberpunk themed wallpaper with neon colors, futuristic cityscape, and dark atmosphere.",
    "Generate an educational infographic style image with icons, charts, and clear information hierarchy.",
    "Create a travel destination poster with beautiful landscape, vibrant colors, and inspiring typography.",
    "Write a prompt for a healthy food flat-lay photo with natural lighting, fresh ingredients, and appetizing composition.",
    "Generate a fitness app hero image with active people, motivational atmosphere, and modern design.",
    "Create a modern business meeting illustration with diverse professionals, collaborative environment, and professional setting.",
    "Write a prompt for a classroom learning scene with engaged students, teacher, and educational materials.",
    "Generate a sports event promotional banner with dynamic action, team colors, and exciting atmosphere.",
  ],
  learn: [
    "Explain the basics of machine learning in simple terms.",
    "Teach me the core concepts of object-oriented programming.",
    "Explain how the stock market works for beginners.",
    "Give me a crash course on digital marketing.",
    "Teach me the fundamentals of web development.",
    "Explain cloud computing with real-life examples.",
    "Help me understand blockchain and cryptocurrencies.",
    "Teach me the basics of SQL databases.",
    "Explain REST APIs and how they work.",
    "Give me an overview of cybersecurity best practices.",
    "Teach me how to structure a good presentation.",
    "Explain growth mindset vs fixed mindset.",
    "Teach me the basics of UI/UX design.",
    "Explain how to read financial statements.",
    "Teach me strategies for effective communication.",
  ],
  investment: [
    "Explain mutual funds vs stocks for a beginner.",
    "Create a simple monthly investing plan for salary earners.",
    "Explain the concept of compound interest with examples.",
    "Suggest ways to diversify an investment portfolio.",
    "Explain risk vs return in investments.",
    "Describe different types of investment assets.",
    "Explain SIP (Systematic Investment Plan) in simple terms.",
    "Suggest questions to ask a financial advisor.",
    "Explain index funds and how they work.",
    "Discuss long-term vs short-term investment strategies.",
    "Explain emergency funds and why they are important.",
    "Describe basic tax-saving investment options.",
    "Explain the concept of dollar-cost averaging.",
    "Suggest books to learn personal finance basics.",
    "Explain how inflation affects savings and investments.",
  ],
  restaurant: [
    "Suggest a themed menu for a family dinner night.",
    "Create a restaurant review for an Italian place.",
    "Suggest healthy options to order at a restaurant.",
    "Help me write feedback for a restaurant service issue.",
    "Suggest marketing ideas for a new restaurant launch.",
    "Create social media post text for a restaurant offer.",
    "Suggest a menu layout for a multi-cuisine restaurant.",
    "Generate tagline ideas for a coffee shop.",
    "Suggest combo meal ideas for office lunches.",
    "Create a short description for a signature dish.",
    "Suggest loyalty program ideas for a restaurant.",
    "Help me respond to an online restaurant review.",
    "Suggest weekend special menu ideas.",
    "Create a script for staff greeting customers.",
    "Suggest Instagram caption ideas for food photos.",
  ],
  travel: [
    "Plan a 3-day itinerary for a beach destination.",
    "Suggest packing list for an international trip.",
    "Help me plan a budget-friendly weekend getaway.",
    "Suggest travel safety tips for solo travelers.",
    "Create a travel checklist before leaving home.",
    "Plan a Europe trip for two weeks.",
    "Suggest underrated travel destinations in Asia.",
    "Help me write an out-of-office message for vacation.",
    "Plan a road trip itinerary with stops and activities.",
    "Suggest questions to ask a travel agent.",
    "Create a travel blog outline for my last trip.",
    "Suggest travel-friendly productivity tips.",
    "Plan a team offsite retreat with activities.",
    "Suggest kid-friendly attractions in a big city.",
    "Create a packing list for a business trip.",
  ],
  fitness: [
    "Create a beginner-friendly home workout plan.",
    "Suggest a weekly gym routine for weight loss.",
    "Explain the difference between cardio and strength training.",
    "Suggest stretches to relieve back and neck pain.",
    "Create a morning routine for better energy.",
    "Suggest desk exercises for people who sit all day.",
    "Create a 30-day fitness challenge plan.",
    "Suggest healthy snack ideas for office workers.",
    "Explain how to track fitness progress effectively.",
    "Create a workout playlist theme idea list.",
    "Suggest habits to improve sleep quality.",
    "Create a hydration reminder plan for the day.",
    "Suggest low-impact exercises for beginners.",
    "Explain warm-up and cool-down importance.",
    "Create a simple yoga routine for relaxation.",
  ],
  project: [
    "Create a React + Node.js full-stack project structure.",
    "Generate a Python Django project structure with API endpoints.",
    "Set up a Next.js project with TypeScript and Tailwind CSS.",
    "Create a mobile app project structure (React Native).",
    "Generate a microservices architecture project structure.",
    "Set up a MERN stack project (MongoDB, Express, React, Node).",
    "Create a Vue.js + Express.js project structure.",
    "Generate a Flutter mobile app project structure.",
    "Set up a Python FastAPI project with PostgreSQL.",
    "Create a Laravel PHP project structure with Vue frontend.",
    "Generate a Go (Golang) REST API project structure.",
    "Set up a Ruby on Rails project structure.",
    "Create a .NET Core Web API project structure.",
    "Generate a Spring Boot Java project structure.",
    "Set up a SvelteKit project with TypeScript.",
  ],
};

export function EmptyChatState({ onNewChat }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [input, setInput] = useState("");
  const [selectedSector, setSelectedSector] = useState(null);
  const { addMessage, startNewChat, currentChat } = useContext(ChatContext);
  const { user } = useAuth();

  // Get user name for greeting
  const userName = user?.name || user?.email?.split("@")[0] || "there";

  const handleSend = async () => {
    if (!input.trim()) return;
    const messageContent = input.trim();
    setInput("");
    try {
      await addMessage({
        role: "user",
        content: messageContent,
        files: [],
      });
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleQuickActionSelect = (sectorKey) => {
    setSelectedSector(sectorKey);
  };

  const handlePromptClick = async (prompt) => {
    try {
      // Clear selected sector to close the prompt list
      setSelectedSector(null);
      
      // Send the message - addMessage will automatically handle startNewChat if currentChat is null
      // and will show processing states from chatcontext.jsx
      await addMessage({
        role: "user",
        content: prompt,
        files: [],
      });
    } catch (error) {
      console.error("Error sending quick action prompt:", error);
      // Show user-friendly error message
      const errorMessage = error.message || "Failed to send message. Please try again.";
      alert(`Error: ${errorMessage}`);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, sm: 4, md: 6 },
        py: 6,
        bgcolor: "transparent",
      }}
    >
      {/* Welcome Card */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 4,
          background: "#ffffff",
          p: { xs: 3, sm: 4, md: 5 },
          mb: 4,
          border: "1px solid #e2e8f0",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Greeting */}
        <Typography
          variant="h4"
          sx={{
            color: "#1e3a8a",
            mb: 1,
            fontWeight: 700,
            fontSize: { xs: "1.75rem", sm: "2.25rem" },
          }}
        >
          Hi {userName} 👋
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "#64748b",
            mb: 4,
            fontWeight: 500,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          How can I help you today?
        </Typography>

        {/* Input Field */}
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            background: "#f8fafc",
          borderRadius: 3,
          px: 2,
          py: 1.5,
          mb: 3,
          border: "1px solid #e2e8f0",
        }}
      >
          <IconButton
            sx={{
              color: "#64748b",
              mr: 1,
              "&:hover": { color: "#1e3a8a" },
            }}
          >
            <Mic fontSize="small" />
          </IconButton>
          <IconButton
            sx={{
              color: "#64748b",
              mr: 1,
              "&:hover": { color: "primary.main" },
            }}
          >
            <KeyboardArrowDown fontSize="small" />
          </IconButton>
          <TextField
            fullWidth
            placeholder="Type or speak your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: {
                color: "white",
                fontSize: "1rem",
                "&::placeholder": {
                  color: "#60a5fa",
                },
              },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim()}
            sx={{
              color: input.trim() ? "#38bdf8" : "#3b82f6",
              ml: 1,
              "&:hover": { bgcolor: "rgba(56, 189, 248, 0.1)" },
            }}
          >
            <Send fontSize="small" />
          </IconButton>
        </Paper>

        {/* Quick Actions */}
        <Typography
          variant="subtitle2"
          sx={{
            color: "#bfdbfe",
            mb: 2,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          Quick Actions
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            justifyContent: "flex-start",
          }}
        >
          {quickActions.map((action) => {
            const IconComponent = action.icon;
            const isSelected = selectedSector === action.key;
            return (
              <Chip
                key={action.key}
                icon={<IconComponent sx={{ color: "inherit !important" }} />}
                label={action.label}
                onClick={() => handleQuickActionSelect(action.key)}
                sx={{
                  bgcolor: isSelected
                    ? "rgba(56, 189, 248, 0.25)"
                    : "rgba(15, 23, 42, 0.9)",
                  color: isSelected ? "#e0f2fe" : "#e5e7eb",
                  border: "1px solid rgba(148, 163, 184, 0.45)",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(56, 189, 248, 0.35)",
                    borderColor: "#38bdf8",
                    color: "#e0f2fe",
                  },
                  height: 40,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            );
          })}
        </Box>

        {selectedSector && sectorPrompts[selectedSector] && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: "#bfdbfe",
                mb: 1.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 1,
              }}
            >
              Suggested Prompts
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                maxHeight: 220,
                overflowY: "auto",
                pr: 0.5,
              }}
            >
              {sectorPrompts[selectedSector].map((prompt) => (
                <Chip
                  key={prompt}
                  label={prompt}
                  onClick={() => handlePromptClick(prompt)}
                  sx={{
                    bgcolor: "rgba(15, 23, 42, 0.9)",
                    color: "#e5e7eb",
                    border: "1px solid rgba(148, 163, 184, 0.4)",
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: "rgba(56, 189, 248, 0.25)",
                      borderColor: "#38bdf8",
                      color: "#e0f2fe",
                    },
                    fontSize: "0.8rem",
                    fontWeight: 400,
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export function EmptyChatListState({ onNewChat }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
        px: 2,
        textAlign: "center",
      }}
    >
      <ChatBubbleOutlineIcon
        sx={{
          fontSize: 48,
          color: "#3b82f6",
          mb: 1.5,
        }}
      />
      <Typography
        variant="body2"
        sx={{
          color: "#60a5fa",
          mb: 2,
        }}
      >
        No chats yet
      </Typography>
      {onNewChat && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            color: "primary.main",
            borderColor: "primary.main",
            "&:hover": { borderColor: "primary.dark" },
          }}
        >
          Start Chat
        </Button>
      )}
    </Box>
  );
}


